import { computed, ref } from 'vue'
import { isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { useTableService } from '@/global/composables/table/useTableService'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'
import { useActiveStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useActiveStatus'
import { toProviderStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/providerData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  BASES_ENDPOINT,
  BASES_TABLE_KEY,
  BASES_TRANSLATION_NAMESPACE as NS,
  BASES_UI_VIEW,
  BASE_DETAILS_ROUTE
} from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: BASES_ENDPOINT
  }
}

export function useTable (options) {
  const {
    trans,
    router,
    openInfoDialogFn,
    closeInfoDialogFn,
    setInfoDialogLoaderFn
  } = options || {}

  const { statusCellConfig, buildStatusCell, activeFilterItems } = useActiveStatus({ trans })

  const isCreating = ref(false)

  const startCreating = () => {
    isCreating.value = true
  }

  const resetCreating = () => {
    isCreating.value = false
  }

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[BASES_TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onShowBaseDetails = (event) => {
    const { id } = event?.data || {}
    if (!id) {
      return
    }

    router.push({
      name: BASE_DETAILS_ROUTE,
      params: {
        id: String(id)
      }
    })
  }

  const deleteBase = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      setInfoDialogLoaderFn(true)
      await api(WINTER_SERVICE_MODULE).delete(`${BASES_ENDPOINT}/${id}`)
      closeInfoDialogFn()
      fetchTableData()
    }
    catch (exception) {
      console.error(exception)
    }
    finally {
      setInfoDialogLoaderFn(false)
    }
  }

  const onBaseDelete = (event) => {
    openInfoDialogFn({
      title: trans(`${NS}.delete_confirmation_title`),
      message: trans(`${NS}.delete_confirmation_message`),
      onConfirm: () => deleteBase(event)
    })
  }

  // The listing reports a base nested - the address sits on `geolocation`, the
  // contact on `responsible_person`, the status is a boolean - so the columns read
  // their value off those instead of a flat key.
  const cellsConfig = computed(() => ({
    address: {
      custom_display_value: (data) => data?.geolocation?.address || ''
    },
    responsible_person: {
      custom_display_value: (data) => data?.responsible_person?.name || ''
    },
    status: {
      ...statusCellConfig,
      custom_display_value: (data) => buildStatusCell(toProviderStatus(data))
    }
  }))

  // Only what the listing reports, and `sortable` only where it can order by it.
  const headers = computed(() => ([
    {
      label: trans(`${NS}.id`),
      key: 'id',
      always_visible: true
    },
    {
      label: trans(`${NS}.base_name`),
      key: 'name',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.associated_base`),
      key: 'associated_base_name',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.address`),
      key: 'address',
      always_visible: true
    },
    {
      label: trans(`${NS}.responsible_person`),
      key: 'responsible_person',
      sortable: true,
      always_visible: true
    },
    {
      label: trans(`${NS}.status`),
      key: 'status',
      always_visible: true
    }
  ]))

  // Only the filters the listing endpoint actually understands - the base's name,
  // the responsible person and whether it is still in use.
  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: trans(`${NS}.base_name`)
      },
      {
        key: 'responsible_person_name',
        label: trans(`${NS}.responsible_person`)
      },
      {
        key: 'is_active',
        label: trans(`${NS}.status`),
        type: 'autocomplete',
        items: activeFilterItems.value,
        autocomplete_options: {
          // A fixed pair of choices - there is nothing to look up remotely.
          allowSearch: false
        }
      }
    ]
  }))

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowBaseDetails),
    deleteAction(trans, onBaseDelete)
  ]))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.bases')
      },
      {
        text: trans(`${NS}.overview`),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: BASES_TABLE_KEY,
      uiView: BASES_UI_VIEW,
      apiConfig: API_CONFIG,
      rowDblClick: onShowBaseDetails,
      search_and_buttons_config: {
        // Free text over the columns the table shows - the base's name, the base it
        // is paired with, the address and the responsible person. The filters dialog
        // stays for the narrower, per-field lookups.
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        showNewBtn: true,
        newClick: startCreating,
        newButtonConfig: {
          label: trans(`${NS}.new_base`)
        }
      },
      // Refresh, filters, columns visibility and the table/grid switcher are all
      // on by default - buildConfig fills them in.
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    isCreating,
    resetCreating,
    fetchTableData
  }
}
